import React from "react";
import Nav from "../../components/layouts/nav";
import { API_URL, DEFAULT_COUNTRY_ID } from "../../_config";
import { showDate } from "../../_common/functions";
import { useParams } from "react-router-dom";
import RightBlogList from "./RightBlogList";
import Footer from "../../components/layouts/footer";
import BottomBlogList from "./BottomBlogList";
import MetaSection from "../../components/MetaSection";
import { useSelectedCountry } from "../../_common/hooks/authHook";
import { IMAGE_FETCH_BACKEND_URL } from "../../_config/site_urls";
import { applyButtonGroupStyles } from "../../utils/applyButtonGroupStyles";
import { buildArticleStructuredData } from "../../utils/structuredData";
import { dedupeById } from "../../_common/functions";
import Loader from "../../components/loader";
// import "./PageDetails.scss";

const BlogDetails = () => {
    // console.log(IMAGE_FETCH_BACKEND_URL);
    const { slug } = useParams<{ slug: string }>();
    const [detailsData, setDetailsData] = React.useState<any>(null);
    const [notFound, setNotFound] = React.useState<boolean>(false); // NEW
    const [blogList, setBlogList] = React.useState<any>(null);
    const [template, setTemplate] = React.useState<any>(1);
    const [processedHtml, setProcessedHtml] = React.useState<string>("");
    const [hasFaqAccordion, setHasFaqAccordion] = React.useState<boolean>(false);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const leftColRef = React.useRef<HTMLDivElement>(null);
    const rightScrollRef = React.useRef<HTMLDivElement>(null);
    const selectedCountry: any = useSelectedCountry();
    const [blogPage, setBlogPage] = React.useState<number>(1);
    const [hasMoreBlogs, setHasMoreBlogs] = React.useState<boolean>(false);
    const [sidebarLoading, setSidebarLoading] = React.useState<boolean>(false);

    const getBlogDetails = async (_slug: string) => {
        try {
            const response = await fetch(API_URL.BLOG_NEWS.BLOG_DETAILS(_slug, selectedCountry?.id || DEFAULT_COUNTRY_ID, 1));
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const resp = await response.json();
            // console.log(resp.data?.blog_details[0]);
            const blog = resp.data?.blog_details?.[0];

            if (blog) {
                setDetailsData(blog);
                setNotFound(false);
            } else {
                setDetailsData(null);   // no data — stays falsy
                setNotFound(true);
            }
            // setDetailsData(resp.data?.blog_details[0])
            const initialBlogs = resp.data?.blogs?.data || [];
            setBlogList(initialBlogs);
            setBlogPage(1);
            setHasMoreBlogs(initialBlogs.length > 0 && 1 < (resp.data?.blogs?.last_page || 1));
        } catch (error) {
            console.log('Error occurred while making the API request.');
            // Handle error
            setDetailsData(null);
            setBlogList([]);
            setNotFound(true);
        }
    }

    const loadMoreBlogs = async () => {
        if (sidebarLoading || !hasMoreBlogs || !slug) return;
        setSidebarLoading(true);
        try {
            const nextPage = blogPage + 1;
            const response = await fetch(API_URL.BLOG_NEWS.BLOG_DETAILS(slug, selectedCountry?.id || DEFAULT_COUNTRY_ID, nextPage));
            const resp = await response.json();
            const newItems = resp.data?.blogs?.data || [];
            setBlogList((prev: any[]) => dedupeById([...prev, ...newItems]));
            setBlogPage(nextPage);
            if (newItems.length === 0 || nextPage >= (resp.data?.blogs?.last_page || 1)) {
                setHasMoreBlogs(false);
            }
        } catch (error) {
            console.log('Error loading more blogs.');
        } finally {
            setSidebarLoading(false);
        }
    };

    const processHtmlForAccordion = (htmlString: any) => {
        if (!htmlString) return { html: "", hasFaq: false };
        if (typeof window === 'undefined') return { html: htmlString, hasFaq: false };

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");

        // Check if faq-accordion class exists
        const faqContainers = doc.querySelectorAll("div.faq-accordion, div[class*='faq-accordion']");
        const hasFaq = faqContainers.length > 0;

        // Check if accordion classes exist
        // const accordionContainers = doc.querySelectorAll("div.accordion-one-col, div.accordion-two-col, div[class*='accordion-one-col'], div[class*='accordion-two-col']");
        // const hasAccordion = accordionContainers.length > 0;
        if (hasFaq) {
            // Add style to hide Bootstrap's default accordion icon and ensure custom icon positioning
            const style = doc.createElement("style");
            style.textContent = `
                .faq-accordian .accordion-button::after {
                    display: none !important;
                }
                .faq-accordian .accordion-button {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                }
            `;
            doc.head.appendChild(style);
            // Convert FAQ structure to Bootstrap accordion format
            faqContainers.forEach((container, containerIndex) => {
                // Add accordion wrapper class
                container.classList.add("accordion", "faq-accordian");
                container.setAttribute("id", `blogAccordion${containerIndex}`);

                // Find all FAQ items (including nested in faq-column)
                const faqItems = container.querySelectorAll(".faq-item");
                let itemIndex = 0;

                faqItems.forEach((item: Element) => {
                    const questionContainer = item.querySelector(".faq-question");
                    const answerContainer = item.querySelector(".faq-answer");

                    if (questionContainer && answerContainer) {
                        // Convert to accordion-item structure
                        item.classList.add("accordion-item");

                        // Get question text (from h4, h3, h5, or text content)
                        const questionHeading = questionContainer.querySelector("h2,h4, h3, h6, h5");
                        let questionText = "";
                        if (questionHeading) {
                            questionText = questionHeading.textContent || questionHeading.innerHTML || "";
                        } else {
                            // Clone to remove toggle
                            const questionClone = questionContainer.cloneNode(true) as Element;
                            const toggle = questionClone.querySelector(".faq-toggle");
                            if (toggle) {
                                toggle.remove();
                            }
                            questionText = questionClone.textContent || questionClone.innerHTML || "";
                        }

                        // Remove toggle from original if exists
                        const toggle = questionContainer.querySelector(".faq-toggle");
                        if (toggle) {
                            toggle.remove();
                        }

                        // Create accordion header structure
                        const headerId = `heading${containerIndex}_${itemIndex}`;
                        const collapseId = `collapse${containerIndex}_${itemIndex}`;

                        const accordionHeader = doc.createElement("h2");
                        accordionHeader.className = "accordion-header";
                        accordionHeader.id = headerId;

                        const button = doc.createElement("button");
                        button.className = "accordion-button collapsed";
                        button.type = "button";
                        button.setAttribute("data-bs-toggle", "collapse");
                        button.setAttribute("data-bs-target", `#${collapseId}`);
                        button.setAttribute("aria-expanded", "false");
                        button.setAttribute("aria-controls", collapseId);

                        // Preserve inline background-color from the original .faq-question div
                        const questionEl = questionContainer as HTMLElement;
                        const inlineBg = questionEl.style.backgroundColor;
                        if (inlineBg) {
                            button.style.setProperty('background-color', inlineBg, 'important');
                        }

                        // Add question text on the left
                        const questionSpan = doc.createElement("span");
                        questionSpan.textContent = questionText.trim();
                        questionSpan.style.flex = "1";
                        questionSpan.style.textAlign = "left";
                        questionSpan.style.paddingRight = "10px";
                        button.appendChild(questionSpan);
                        // Add plus/minus icon on the right side
                        const iconSpan = doc.createElement("span");
                        iconSpan.className = "faq-accordion-icon";
                        // iconSpan.textContent = "+";
                        iconSpan.style.marginLeft = "auto";
                        iconSpan.style.fontSize = "1.5em";
                        iconSpan.style.fontWeight = "bold";
                        iconSpan.style.flexShrink = "0";
                        iconSpan.style.lineHeight = "1";
                        iconSpan.style.minWidth = "24px";
                        iconSpan.style.textAlign = "center";
                        iconSpan.style.display = "inline-flex";
                        iconSpan.style.alignItems = "center";
                        iconSpan.style.justifyContent = "center";
                        button.appendChild(iconSpan);
                        accordionHeader.appendChild(button);

                        // Create accordion collapse structure
                        const collapseDiv = doc.createElement("div");
                        collapseDiv.id = collapseId;
                        collapseDiv.className = "accordion-collapse collapse";
                        collapseDiv.setAttribute("aria-labelledby", headerId);
                        collapseDiv.setAttribute("data-bs-parent", `#blogAccordion${containerIndex}`);

                        const accordionBody = doc.createElement("div");
                        accordionBody.className = "accordion-body";
                        accordionBody.innerHTML = answerContainer.innerHTML;

                        collapseDiv.appendChild(accordionBody);

                        // Add event listeners to toggle icon when accordion opens/closes
                        // Use Bootstrap's collapse events for accurate state tracking
                        collapseDiv.addEventListener('show.bs.collapse', () => {
                            iconSpan.textContent = "−";
                            button.setAttribute("aria-expanded", "true");
                            button.classList.remove("collapsed");
                        });
                        collapseDiv.addEventListener('hide.bs.collapse', () => {
                            iconSpan.textContent = "+";
                            button.setAttribute("aria-expanded", "false");
                            button.classList.add("collapsed");
                        });
                        // Also handle click event as fallback for immediate feedback
                        button.addEventListener("click", () => {
                            setTimeout(() => {
                                const isExpanded = button.getAttribute("aria-expanded") === "true";
                                iconSpan.textContent = isExpanded ? "−" : "+";
                            }, 50);
                        });

                        // Replace question and answer with accordion structure
                        questionContainer.replaceWith(accordionHeader);
                        answerContainer.replaceWith(collapseDiv);

                        itemIndex++;
                    }
                });
            });
        }

        // Fix image src paths
        const images = doc.querySelectorAll("img");
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src && src.startsWith("..")) {
                const cleanedSrc = src.replace(/^(\.\.\/)+/, "");
                img.setAttribute("src", `${IMAGE_FETCH_BACKEND_URL}/${cleanedSrc}`);
            }
        });

        let finalHtml = doc.body.innerHTML;
        finalHtml = finalHtml.replace(/(<p>\s*);/g, '$1');

        return { html: finalHtml, hasFaq };
    };

    const fixImageSrcPaths = (htmlString: any) => {
        if (!htmlString) return "";
        if (typeof window === 'undefined') return htmlString;

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");

        // Fix image src paths
        const images = doc.querySelectorAll("img");
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src && src.startsWith("..")) {
                const cleanedSrc = src.replace(/^(\.\.\/)+/, "");
                img.setAttribute("src", `${IMAGE_FETCH_BACKEND_URL}/${cleanedSrc}`);
            }
        });

        let finalHtml = doc.body.innerHTML;

        finalHtml = finalHtml.replace(/(<p>\s*);/g, '$1');

        return finalHtml;
    };

    React.useEffect(() => {
        if (slug) {
            getBlogDetails(slug);
        }
    }, [slug, selectedCountry]);

    React.useEffect(() => {
        if (detailsData?.long_description) {
            const { html, hasFaq } = processHtmlForAccordion(detailsData.long_description);
            setProcessedHtml(html || fixImageSrcPaths(detailsData.long_description) || "");
            setHasFaqAccordion(hasFaq);
        } else {
            setProcessedHtml("");
            setHasFaqAccordion(false);
        }
    }, [detailsData]);

    // Apply Button Group data-attribute styles + initialize Bootstrap accordion after render
    React.useEffect(() => {
        if (contentRef.current) {
            applyButtonGroupStyles(contentRef.current);
        }
        if (hasFaqAccordion && contentRef.current) {
            const accordionElements = contentRef.current.querySelectorAll('[data-bs-toggle="collapse"]');
            accordionElements.forEach((_element) => {
                if (typeof window !== 'undefined' && (window as any).bootstrap) {
                    // Bootstrap is available, accordion should work
                }
            });
        }
    }, [hasFaqAccordion, processedHtml]);

    // Sync right sidebar height to left content column height
    React.useEffect(() => {
        if (!leftColRef.current || !rightScrollRef.current) return;
        const syncHeight = () => {
            requestAnimationFrame(() => {
                if (leftColRef.current && rightScrollRef.current) {
                    rightScrollRef.current.style.maxHeight = `${leftColRef.current.offsetHeight}px`;
                }
            });
        };
        syncHeight();
        const resizeObserver = new ResizeObserver(syncHeight);
        resizeObserver.observe(leftColRef.current);
        return () => resizeObserver.disconnect();
    }, [processedHtml, template]);

    if (detailsData === null && !notFound) {
        return <Loader />;
    }

    
    return (
        <div className='bg-white'>
            {detailsData ? (
                <MetaSection
                    title={detailsData.meta_title || detailsData.title}
                    desc={detailsData.meta_description || detailsData.short_description}
                    keywords={detailsData.meta_keyword || "blog, details"}
                    image={detailsData.blog_thumb?.original || ""}
                    url={`/blog/${slug}`}
                    type="article"
                    structuredData={buildArticleStructuredData({
                        type: "BlogPosting",
                        headline: detailsData.title,
                        authorName: detailsData.author_name,
                        datePublished: detailsData.published_date,
                        image: detailsData.blog_thumb?.original,
                        url: `/blog/${slug}`,
                    })}
                />
            ) : (
                <MetaSection
                    title="Blog Details"
                    desc="Blog details page"
                    keywords="blog, details"
                    url={`/blog/${slug}`}
                    type="article"
                    noindex={notFound}
                />
            )}
            {/* <MetaSection
                title={detailsData?.meta_title || "Blog Details"}
                desc={detailsData?.meta_description || "Blog details page"}
                keywords={detailsData?.meta_keyword || "blog, details"}
                image={detailsData?.blog_thumb?.original || ""}
                url={`/blog/${slug}`}
                type="article"
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'BlogPosting',
                    headline: detailsData?.title,
                    author: { '@type': 'Person', name: detailsData?.author_name },
                    datePublished: detailsData?.published_date,
                    image: detailsData?.blog_thumb?.original,
                    url: `${SITE_URL}/blog/${slug}`
                }}
            /> */}
            <Nav />
            <div className="subscription">
                <div className="container">
                    <div className="banner-text latest_news_title">
                        <h2 data-aos="fade-up" className="text-left pb-1">
                            {detailsData?.title} </h2>
                    </div>
                    <div className='row'>
                        <div ref={leftColRef} className={` col-lg-${template == 1 ? '8' : '12'}  col-md-12`}>
                            <div className="news_detailscontent">
                                {
                                    detailsData === null || !detailsData && <div className="text-center my-5">This article is unavailable in your region.</div>
                                }
                            {/* Loading... */}
                                <div className="news_details_image">
                                    <img src={detailsData?.blog_thumb?.original} alt={detailsData?.title} />
                                    <div className="news_details_date">
                                        <span className="details_date">{showDate(detailsData?.published_date, 'DD')}</span>
                                        <span className="details_month">{showDate(detailsData?.published_date, 'MMM')}</span>
                                    </div>
                                </div>
                                <h3 className="news_detailstitle">{detailsData?.author_name}</h3>
                                <div className="news_detailstxt" ref={contentRef}>
                                    <div dangerouslySetInnerHTML={{ __html: processedHtml || (detailsData?.long_description ? fixImageSrcPaths(detailsData.long_description) : "") }} />
                                </div>
                            </div>
                        </div>

                        {
                            template === 1 ?
                                <div className="col-lg-4 col-md-12" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div
                                        ref={rightScrollRef}
                                        style={{ overflowY: 'auto', flex: 1 }}
                                    >
                                        <RightBlogList
                                            blogList={blogList}
                                            onLoadMore={loadMoreBlogs}
                                            hasMore={hasMoreBlogs}
                                            isLoading={sidebarLoading}
                                            scrollContainerRef={rightScrollRef}
                                        />
                                    </div>
                                </div> :
                                <BottomBlogList blogList={blogList} />
                        }

                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}
export default BlogDetails;